// @ts-nocheck
import React, { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useHistory } from 'react-router-dom';
import intersectionWith from 'lodash/intersectionWith';
import classNames from 'classnames';

import LogoPage from 'components/pages/LogoPage';
import { Focusable } from 'components/focus';

import WithLoading from 'components/common/WithLoading';
import Image from 'components/common/Image';
import QrCode from 'components/common/QrCode';

import { useCurrentSubscriptions } from 'hooks/auth/subscriptions';
import useShortCodeFlow from 'hooks/auth/shortCode';
import { useFocusManager } from 'hooks/focus';
import useTimeout from 'hooks/timeout';
import { useVideoById } from 'hooks/video';
import useTracking from 'hooks/analytics';
import { useShortCodeQrCode } from 'hooks/auth/qrCode';

import { appendSiteHierPrefix } from 'support/analytics/analyticsHelper';
import textToSpeechManager from 'support/tts/TextToSpeechManager';
import { getSplitChars } from 'support/textToSpeechHelper';

import { useParamountPlusDisplayName } from 'hooks/kitchenSink';
import { Providers } from 'app-types';
import styles from './ShortCode.module.scss';

const messages = {
  signInLabel: {
    id: 'shortCode.signInLabel',
    description: 'Sign In Label',
  },
  signInTextDual: {
    id: 'shortCode.signInText.dual',
    description: 'Sign in text for authentication with both adobe and P+',
  },
  signInTextAdobe: {
    id: 'shortCode.signInText.adobe',
    description: 'Sign in text for authentication with adobe',
  },
  signInTextPP: {
    id: 'shortCode.signInText.paramounPlus',
    description: 'Sign in text for authentication with P+',
  },
  signInHeaderDual: {
    id: 'shortCode.signInText.dual.header',
    description: 'Sign in header for authentication with both adobe and P+',
  },
  signInHeaderAdobe: {
    id: 'shortCode.signInText.adobe.header',
    description: 'Sign in header for authentication with both adobe',
  },
  signInHeaderPP: {
    id: 'shortCode.signInText.paramounPlus.header',
    description: 'Sign in header for authentication with P+',
  },
  signInSuccess: {
    id: 'shortCode.signIn.success',
    description: 'Sign in success',
  },
  shortCodeLabel: {
    id: 'shortCode.label',
    description: 'Label behind or on top of the short code',
  },
  shortCodeCenterText: {
    id: 'shortCode.centerText',
    description: 'Text between shortcode and qr code',
  },
  qrCodeTextLine1: {
    id: 'shortCode.qrCode.line1',
    description: 'First line of text for qr code',
  },
  qrCodeTextLine2: {
    id: 'shortCode.qrCode.line2',
    description: 'Second line of text for qr code',
  },
};

const useProviders = () => {
  const params = new URLSearchParams(useLocation().search);
  const providersQuery = params.get('providers');

  return useMemo(() => providersQuery.split(','), [providersQuery]);
};

const useReferringVideo = referringLocation => {
  let redirectedVideoId = null;
  if (referringLocation && referringLocation.search) {
    const params = new URLSearchParams(referringLocation.search);
    redirectedVideoId = params.get('video');
  }
  const [video] = useVideoById(redirectedVideoId);
  return video;
};

const useActivatedProvider = providers => {
  const currentSubscriptions = useCurrentSubscriptions();

  const [provider] = intersectionWith(
    currentSubscriptions,
    providers,
    (a, b) => a.toLowerCase() === b.toLowerCase(),
  );

  return provider;
};

const trackShortCodeIssued = ({
  tracker,
  shortCode,
  referringVideo,
  isAdobe,
  isParamountPlus,
}) => {
  const getClickText = () => {
    if (referringVideo) return referringVideo?.trackingId || '';
    if (isAdobe) return 'mvpd sign in';
    if (isParamountPlus) return 'aa sign in';
    return null;
  };

  const pageData = {
    pageName: 'auth_code',
    pageType: 'account',
    siteHier: appendSiteHierPrefix('shortcode'),
    clickText: getClickText(),
    authShortCode: shortCode,
    authShortCodeIssued: 1,
  };

  tracker(pageData);
};

const trackShortCodeActivated = ({ tracker, shortCode, provider }) => {
  const pageData = {
    pageName: `authentication-success-${
      provider.toLowerCase() === Providers.ADOBE.toLowerCase() ? 'mvpd' : 'aa'
    }`,
    pageType: 'account',
    siteHier: appendSiteHierPrefix('device refresh'),
    authShortCode: shortCode,
    authShortCodeActivated: 1,
  };

  tracker(pageData);
};

const ShortCode = () => {
  const providers = useProviders();
  const intl = useIntl();

  let ppInHeader = false;
  let showContent = true;
  let providerMessage = null;
  let providerHeader = null;
  let showLogo = false;

  const isAdobe = useMemo(
    () =>
      providers.findIndex(
        item => item.toLowerCase() === Providers.ADOBE.toLowerCase(),
      ) >= 0,
    [providers],
  );
  const isParamountPlus = useMemo(
    () =>
      providers.findIndex(
        item => item.toLowerCase() === Providers.PARAMOUNT_PLUS.toLowerCase(),
      ) >= 0,
    [providers],
  );

  if (isAdobe && isParamountPlus) {
    providerMessage = messages.signInTextDual;
    providerHeader = messages.signInHeaderDual;
    ppInHeader = true;
    showLogo = true;
  } else if (isAdobe) {
    providerMessage = messages.signInTextAdobe;
    providerHeader = messages.signInHeaderAdobe;
  } else if (isParamountPlus) {
    providerMessage = messages.signInTextPP;
    providerHeader = messages.signInHeaderPP;
    ppInHeader = true;
    showLogo = true;
  } else {
    showContent = false;
  }

  const ksLoading = useSelector(state => state.kitchenSink.loading);
  const history = useHistory();
  const { trackPage: tracker } = useTracking();
  const { state: routeState } = useLocation();
  const referringVideo = useReferringVideo(routeState?.redirectTo);

  const onShortCode = shortCode => {
    trackShortCodeIssued({
      tracker,
      shortCode,
      referringVideo,
      isAdobe,
      isParamountPlus,
    });
  };

  const { shortCode, loading, success } = useShortCodeFlow(
    providers,
    onShortCode,
  );

  const activatedProvider = useActivatedProvider(providers);
  useEffect(() => {
    if (activatedProvider && shortCode) {
      trackShortCodeActivated({
        tracker,
        shortCode,
        provider: activatedProvider,
      });
    }
  }, [activatedProvider, shortCode, tracker]);

  useEffect(() => {
    if (success && !loading) {
      history.replace(routeState?.redirectTo || '/', {
        restore: true,
      });
    }
    return () => undefined;
  }, [success, loading, history, routeState]);

  // Return the user when shortcode expires after 10 minutes
  useTimeout(() => history.goBack(), 600000);

  const paramountPlusLogoUrl = useSelector(state => {
    try {
      return state.kitchenSink.data.providers.paramountplus.logoMediumUrl;
    } catch {
      return null;
    }
  });
  const paramountPlusDisplayName = useParamountPlusDisplayName();
  const secondScreenUrl = useSelector(state => {
    try {
      if (isAdobe) {
        return state.kitchenSink.data.providers.adobe.secondScreenUrl;
      }
      return state.kitchenSink.data.providers.paramountplus.secondScreenUrl;
    } catch {
      return null;
    }
  });
  const providerTextValues = useMemo(
    () => ({
      ppName: paramountPlusDisplayName,
      url: <strong className={styles.url}>{secondScreenUrl}</strong>,
    }),
    [paramountPlusDisplayName, secondScreenUrl],
  );
  const providerTtsValues = useMemo(
    () => ({
      ppName: paramountPlusDisplayName,
      url: secondScreenUrl,
    }),
    [paramountPlusDisplayName, secondScreenUrl],
  );

  const focusManager = useFocusManager();
  useEffect(() => {
    focusManager.setCurrentFocus('short-code-page');
  }, [focusManager]);

  useEffect(() => {
    if (!showContent || !shortCode) return () => undefined;
    textToSpeechManager.clear();
    textToSpeechManager.add(intl.formatMessage(messages.signInLabel));
    textToSpeechManager.add(intl.formatMessage(providerHeader));
    if (ppInHeader) {
      textToSpeechManager.add(paramountPlusDisplayName);
    }
    textToSpeechManager.add(
      intl.formatMessage(providerMessage, providerTtsValues),
    );
    textToSpeechManager.add(intl.formatMessage(messages.shortCodeLabel));
    textToSpeechManager.add(getSplitChars(shortCode));
    textToSpeechManager.add(intl.formatMessage(messages.shortCodeCenterText));
    textToSpeechManager.add(intl.formatMessage(messages.qrCodeTextLine1));
    textToSpeechManager.add(intl.formatMessage(messages.qrCodeTextLine2));
    textToSpeechManager.speak();

    return () => {
      // stop current tts if reread
      textToSpeechManager.stop();
    };
  }, [
    paramountPlusDisplayName,
    intl,
    providerMessage,
    secondScreenUrl,
    shortCode,
    showContent,
    ppInHeader,
    providerTextValues,
    providerTtsValues,
    providerHeader,
  ]);

  const { qrCodeUrl } = useShortCodeQrCode(secondScreenUrl, shortCode);

  return (
    <Focusable
      focusId="short-code-page"
      className="w-100 h-100"
      data-testid="short-code-page"
      render={() => (
        <WithLoading loading={loading || ksLoading}>
          <LogoPage className="position-relative">
            {showContent && (
              <div
                data-testid="message-content"
                className={classNames(
                  'd-flex flex-column align-items-center',
                  styles.messageContent,
                )}
              >
                <span className={styles.signInLabel}>
                  <FormattedMessage {...messages.signInLabel} />
                </span>
                <span className={styles.signInHeader}>
                  <FormattedMessage {...providerHeader} />
                  {showLogo && (
                    <Image
                      className={styles.headerLogo}
                      src={paramountPlusLogoUrl}
                      alt={paramountPlusDisplayName}
                    />
                  )}
                </span>
                <div
                  className={classNames(
                    'd-flex flex-row justify-content-center',
                    styles.mainRow,
                  )}
                >
                  <span className={styles.leftContent}>
                    <span className={styles.message}>
                      <FormattedMessage
                        {...providerMessage}
                        values={providerTextValues}
                      />
                    </span>
                    <span className={styles.shortCode}>
                      <div className={styles.shortCodeLabel}>
                        <FormattedMessage {...messages.shortCodeLabel} />
                      </div>
                      <div className={styles.shortCodeValue}>{shortCode}</div>
                    </span>
                  </span>
                  <span
                    className={classNames(
                      'd-flex justify-content-center',
                      styles.centerContent,
                    )}
                  >
                    <span className={styles.centerText}>
                      <FormattedMessage {...messages.shortCodeCenterText} />
                    </span>
                    <div className={styles.divider} />
                  </span>
                  <span
                    className={classNames(
                      'd-flex flex-row align-items-center',
                      styles.rightContent,
                    )}
                  >
                    <QrCode url={qrCodeUrl} />
                    <span
                      className={classNames(
                        'd-flex flex-column',
                        styles.qrCodeText,
                      )}
                    >
                      <span className="font-weight-bold">
                        <FormattedMessage {...messages.qrCodeTextLine1} />
                      </span>
                      <span className={styles.qrCodeTextLine2}>
                        <FormattedMessage {...messages.qrCodeTextLine2} />
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            )}
          </LogoPage>
        </WithLoading>
      )}
    />
  );
};

export default ShortCode;
